import { Tabs } from "@repo/ui/Navigation/tabs";
import type { FC } from "react";
import {
  createPath,
  matchPath,
  resolvePath,
  type To,
  useLocation,
  useResolvedPath,
} from "react-router";

export interface RouteTabItem {
  label: string;
  /** Complete route destination, relative to the current route. */
  to: To;
  /**
   * Keep the tab selected on descendant routes too (like `NavLink`'s
   * `end={false}`). Defaults to exact matching.
   */
  matchDescendants?: boolean;
  /**
   * Other destinations, relative like `to`, that keep the tab selected (with
   * the same `matchDescendants`), for a tab whose views live at sibling routes.
   */
  alsoMatches?: readonly To[];
  isDisabled?: boolean;
}

export interface RouteTabsProps {
  tabs: readonly RouteTabItem[];
  ariaLabel?: string;
}

/**
 * The page header's tab strip for router-driven tabs: resolves each tab's
 * relative `to` against the current route and selects the deepest tab whose
 * path matches the location (via `matchPath`, so matching stays
 * case-insensitive like the router's own), so pages only declare their tab
 * targets.
 */
export const RouteTabs: FC<RouteTabsProps> = ({ tabs, ariaLabel }) => {
  const { pathname } = useLocation();
  const basePath = useResolvedPath(".").pathname;

  const resolvedTabs = tabs.map((tab) => ({
    ...tab,
    resolvedPath: resolvePath(tab.to, basePath),
  }));

  // Each tab with the length of its longest destination matching the
  // location, so the deepest match wins.
  const selectedTab = resolvedTabs
    .flatMap((tab) => {
      const matchedLengths = [tab.to, ...(tab.alsoMatches ?? [])]
        .map((to) => resolvePath(to, basePath).pathname)
        .filter(
          (path) =>
            matchPath(
              { path, end: !(tab.matchDescendants ?? false) },
              pathname,
            ) !== null,
        )
        .map((path) => path.length);
      return matchedLengths.length > 0
        ? [{ tab, matchedLength: Math.max(...matchedLengths) }]
        : [];
    })
    .sort((a, b) => b.matchedLength - a.matchedLength)[0]?.tab;

  const items = resolvedTabs.map((tab) => ({
    id: tab.resolvedPath.pathname,
    label: tab.label,
    href: createPath(tab.resolvedPath),
    isDisabled: tab.isDisabled,
  }));

  return (
    <Tabs
      tabs={items}
      selectedTabId={selectedTab?.resolvedPath.pathname}
      ariaLabel={ariaLabel}
    />
  );
};
